import { IP_NOT_ALLOWED, IP_NOT_ALLOWED_MESSAGE } from '@/services/httpError';
import type { NormalizedApiError } from '@/services/types';

/** User-facing copy for the documented MFA Problem Details codes. */
const MFA_ERROR_MESSAGES: Record<string, string> = {
	MFA_NOT_ENABLED:
		'Two-factor authentication is not enabled for this account. Refresh the page and try again.',
	MFA_ENROLLMENT_INVALID:
		'This setup session expired. Start the setup again from the beginning.',
	MFA_CODE_INVALID: 'That code is incorrect. Check the app and try again.',
	MFA_CHALLENGE_INVALID:
		'Your verification session expired. Sign in again to continue.',
	REAUTHENTICATION_REQUIRED:
		'Your password is incorrect, or the request expired. Please try again.',
	STALE_SECURITY_VERSION:
		'Your session is no longer valid because security settings changed. Sign in again.',
	MFA_ALREADY_ENABLED:
		'Two-factor authentication is already enabled. Replace or disable it instead.',
	MFA_ATTEMPTS_EXCEEDED:
		'Too many attempts. Wait a moment before trying again.',
	// Returned by `POST /v1/auth/login` for an offboarded user.
	USER_DISABLED: 'This account has been disabled. Contact your administrator.',
	// The tenant's CMS IP allowlist refused the sign-in.
	[IP_NOT_ALLOWED]: IP_NOT_ALLOWED_MESSAGE,
};

const FALLBACK_MESSAGE = 'Something went wrong. Please try again.';

export interface MfaErrorInfo {
	code: string | null;
	message: string;
	/** Milliseconds to wait before retrying, when the backend provided it. */
	retryAfterMs?: number;
}

function isNormalizedApiError(value: unknown): value is NormalizedApiError {
	return (
		typeof value === 'object' &&
		value !== null &&
		'status' in value &&
		'code' in value &&
		'message' in value
	);
}

/** Map any thrown value to a predictable, user-safe MFA error. */
export function describeMfaError(error: unknown): MfaErrorInfo {
	if (isNormalizedApiError(error)) {
		const retryAfterMs = error.retryAfterMs;
		let message =
			(error.code && MFA_ERROR_MESSAGES[error.code]) ||
			error.message ||
			FALLBACK_MESSAGE;

		if (error.code === 'MFA_ATTEMPTS_EXCEEDED' && retryAfterMs) {
			const seconds = Math.ceil(retryAfterMs / 1000);
			message = `Too many attempts. Try again in ${seconds}s.`;
		}

		return { code: error.code, message, retryAfterMs };
	}

	if (error instanceof Error && error.message) {
		return { code: null, message: error.message };
	}

	return { code: null, message: FALLBACK_MESSAGE };
}
